import React, { useState, useEffect, useRef } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useParams } from "react-router-dom";
import CommunicationMessage from "./CommunicationMessage";
import { useAuth } from "../../context/AuthContext";
import { validateLettersDigitsSpecChars } from "../../utils/Validators";
import { addMessage, setConversationStatus } from "../../services/Application";
import LoadingSpinner from "./LoadingSpinner";

import logger from "../../utils/logger";

function CommunicationPanel({
  isVisible,
  data,
  communicationCallbacks,
  embedded = false,
  modal = false,
  onMessageSend = null,
  caseId = null,
  step = null,
  handleStatusChange,
  persistent = false,
  allowMarkAsRead = false,
  pendingMessageSenderRef = null,
}) {
  const [conversationData, setConversationData] = useState();
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [solved, setSolved] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const { accessToken, userName, checkPermission } = useAuth();
  const messagesEndRef = useRef(null);
  const handleSendRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  let { step: paramStep, id: paramId } = useParams();

  const currentId = caseId || paramId;
  const currentStep = step || paramStep;

  function getFormattedDate() {
    let date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const createMessage = () => {
    let message = {
      author: userName,
      authorRole: checkPermission("Obsługa wniosków")
        ? "operator"
        : "wnioskodawca",
      createDate: getFormattedDate(),
      readDate: "",
      text: messageText,
    };

    return message;
  };

  const handleMessageInput = (e) => {
    const value = e.target.value;

    if (validateLettersDigitsSpecChars(value, 3000, false)) {
      setMessageText(value);
      setSendError(null);
    }
  };

  const handleSend = async () => {
    if (!messageText.trim()) return true;

    setSending(true);
    setSendError(null);

    try {
      await addMessage(
        currentId,
        currentStep,
        conversationData.id,
        messageText,
        accessToken,
      );

      setMessageText("");

      if (!persistent) {
        const isOperator = checkPermission("Obsługa wniosków");
        const nextStatus = isOperator ? "open" : "answered";

        try {
          await setConversationStatus(
            currentId,
            currentStep,
            conversationData.id,
            nextStatus,
            accessToken,
          );
        } catch (statusError) {
          logger.error("Error updating conversation status:", statusError);
        }

        if (!embedded && communicationCallbacks) {
          communicationCallbacks[conversationData.id](nextStatus);
        }

        if (handleStatusChange) {
          handleStatusChange(nextStatus);
        }
      }

      if (onMessageSend) {
        await onMessageSend();
      }

      setTimeout(() => {}, 100);
      return true;
    } catch (error) {
      logger.error("Error sending message:", error);
      setSendError(
        "Nie udało się wysłać wiadomości. Spróbuj ponownie przed wysłaniem wniosku.",
      );
      return false;
    } finally {
      setSending(false);
    }
  };

  handleSendRef.current = handleSend;

  useEffect(() => {
    if (!pendingMessageSenderRef) return undefined;

    const sendPendingMessage = () => handleSendRef.current();
    pendingMessageSenderRef.current = sendPendingMessage;

    return () => {
      if (pendingMessageSenderRef.current === sendPendingMessage) {
        pendingMessageSenderRef.current = null;
      }
    };
  }, [pendingMessageSenderRef]);

  const handleMarkAsRead = async () => {
    if (!conversationData || markingAsRead) return;

    setMarkingAsRead(true);
    try {
      await setConversationStatus(
        currentId,
        currentStep,
        conversationData.id,
        "read",
        accessToken,
      );
      setConversationData((current) => ({ ...current, status: "read" }));

      if (onMessageSend) {
        await onMessageSend();
      }
    } catch (error) {
      logger.error("Error marking conversation as read:", error);
    } finally {
      setMarkingAsRead(false);
    }
  };

  const handleMarkAsSolved = async (e) => {
    const isChecked = e.target.checked;
    setSolved(isChecked);

    try {
      let newData = { ...data };
      newData.status = isChecked
        ? "solved"
        : newData.Conversation?.length > 0
          ? "open"
          : "none";

      if (!embedded && communicationCallbacks) {
        communicationCallbacks[conversationData.id](newData.status);
      }

      await setConversationStatus(
        currentId,
        currentStep,
        conversationData.id,
        newData.status,
        accessToken,
      );

      handleStatusChange(newData.status);

      if (onMessageSend) {
        await onMessageSend();
      }
    } catch (error) {
      logger.error("Error updating conversation status:", error);
      setSolved(!isChecked);
    }
  };

  useEffect(() => {
    if (data) {
      setConversationData(data);
      setMessages(data.Conversation || []);
      setSolved(data.status === "solved");
    }
  }, [data]);

  // useEffect(() => {
  //   if (messages.length > 0) {
  //     setTimeout(scrollToBottom, 100);
  //   }
  // }, [messages.length]);

  if (!isVisible) return null;

  const lastMessage = messages[messages.length - 1];
  const hasUnreadApplicantMessage =
    conversationData?.status === "unread" ||
    (conversationData?.status === "open" &&
      lastMessage?.authorRole === "wnioskodawca");

  if (isVisible)
    return (
      <div
        className={"d-flex flex-column " + (embedded ? "" : "pe-1 pt-2 w19")}
      >
        {!embedded && <h3>WIADOMOŚCI</h3>}

        {conversationData ? (
          <div className="p-0">
            {!embedded && (
              <div className=" d-flex flex-row ps-auto mb-1 w-100">
                {conversationData.locationName && (
                  <div className="ms-0 me-1 small">
                    {conversationData.locationName}
                  </div>
                )}
                <div className="ms-auto me-0 very-small w1">
                  {conversationData.id}
                </div>
              </div>
            )}

            {messages.length > 1 && (
              <div className="d-flex flex-row mb-1">
                <a
                  className="ms-auto"
                  onClick={() => scrollToBottom()}
                  style={{ cursor: "pointer" }}
                >
                  <i className="bi bi-arrow-down-circle"></i>&nbsp;
                  {embedded
                    ? "Przejdź do ostatniej wypowiedzi"
                    : "Ostatnia wypowiedź"}
                </a>
              </div>
            )}

            {messages && messages.length > 0 ? (
              <div className="w-100 mb-1">
                {messages.map((message, index) => (
                  <CommunicationMessage
                    author={message.author}
                    authorRole={message.authorRole}
                    createDate={message.createDate}
                    readDate={message.readDate}
                    text={message.text}
                    key={index}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            ) : (
              <div>Brak wiadomości</div>
            )}
          </div>
        ) : (
          <div>
            <div>Kliknij ikonę komentarza</div>
            <div
              className="rounded-2 border p-0 d-flex justify-content-center align-items-center ms-1"
              style={{
                width: "30px",
                height: "30px",
                borderWidth: "0px",
                backgroundColor: "red",
                color: "white",
                fontSize: "18px",
              }}
            >
              <i className="bi-envelope"></i>
            </div>
            w formularzu, aby otworzyć konwersację.
          </div>
        )}

        {persistent &&
          allowMarkAsRead &&
          conversationData &&
          checkPermission("Obsługa wniosków") &&
          hasUnreadApplicantMessage && (
            <button
              type="button"
              className="btn btn-outline-primary rounded-2 ms-auto mb-2"
              onClick={handleMarkAsRead}
              disabled={markingAsRead}
            >
              <i className="bi bi-check2-circle me-1" aria-hidden="true"></i>
              {markingAsRead ? "Oznaczanie..." : "Oznacz jako przeczytane"}
            </button>
          )}

        {conversationData && (persistent || !solved) && (
          <div
            className={
              "d-flex flex-column " +
              (embedded ? "" : "mb-1 pb-1 border-bottom")
            }
          >
            <textarea
              id="message"
              className="form-control mb-1 h6"
              style={{ backgroundColor: "white" }}
              placeholder="Wprowadź treść wiadomości"
              value={messageText}
              onChange={handleMessageInput}
            />

            {sendError && (
              <div className="alert alert-danger py-2 mb-2" role="alert">
                {sendError}
              </div>
            )}

            {sending ? (
              <button
                type="submit"
                className={
                  "btn btn-primary rounded-2 ms-auto h2 " +
                  (embedded ? "w11" : "w7")
                }
                disabled
              >
                <LoadingSpinner text="Wysyłanie" />
              </button>
            ) : messageText.trim().length > 0 ? (
              <button
                type="submit"
                className={
                  "btn btn-primary rounded-2 ms-auto h2 " +
                  (embedded ? "w11" : "w7")
                }
                onClick={() => handleSend()}
              >
                Wyślij
              </button>
            ) : (
              <button
                type="submit"
                className={
                  "btn btn-primary rounded-2 ms-auto h2 " +
                  (embedded ? "w11" : "w7")
                }
                disabled
              >
                Wyślij
              </button>
            )}
          </div>
        )}

        {!persistent &&
          (!embedded || modal) &&
          conversationData &&
          checkPermission("Obsługa wniosków") && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="mark-as-solved"
                checked={solved}
                onChange={handleMarkAsSolved}
              />
              <label className="form-check-label" htmlFor="mark-as-solved">
                Oznacz jako rozwiązane
              </label>
            </div>
          )}
      </div>
    );
}

export default CommunicationPanel;
